<template>
    <div class="effect-wrapper">

        <div class="grid-x table__filter" v-if="showSortOptions">
            
            <div class="medium-6 cell" >
                <div class="button-group button__filter button__filter--small">
                    <span class="button__label">{{labels.filterAccordingTo}}</span>
                    <a @click="toggleSortListBy('targetGroup')" @keyup.enter="toggleSortListBy('targetGroup')" tabindex="0" class="button" :class="{'outline': sortByLabel !== 'targetGroup'}">{{labels.targetGroup}}</a>
                    <a @click="toggleSortListBy('activity')" @keyup.enter="toggleSortListBy('activity')" tabindex="0" class="button" :class="{'outline': sortByLabel !== 'activity'}">{{labels.intervention}}</a>
                </div> 
            </div>

            <div class="medium-6 cell text-right">
                <div class="button-group button__filter button__filter--small button__filter--small--end">
                    <span class="button__label">{{labels.display}}</span>
                    <a @click="togglePositive(false)" @keyup.enter="togglePositive(false)" tabindex="0" class="button" :class="{'outline': positiveOnly}">{{labels.allResults}}</a>
                    <a @click="togglePositive(true)" @keyup.enter="togglePositive(true)" tabindex="0" class="button" :class="{'outline': !positiveOnly}">{{labels.onlyPositives}}</a>
                </div>
            </div>

        </div>

        <div></div>
        <div class="table__wrapper cell" 
            v-for="(table, name, i) in searchResult" 
            :key="i" 
            :class="{'table__wrapper--loading': searching}"
            v-if="!positiveOnly || anyPositiveRows(table)">
            
            <div class="loader"></div>

            <!-- jobeffects table w. expand/collapse -->
            <table v-if="labels" class="table table--effect table--no-hover table">
                <caption class="table__caption">
                    <h2 class="table--h2">{{name}}</h2>
                </caption>
                <thead>
                    <tr>
                        <th></th>
                        <th v-if="showEffectColumn" class="effects">{{labels.effect}}</th>
                        <th class="studies">{{labels.studies}}</th>
                        <th>{{labels.levelOfEvidence}}</th>
                        <th>{{labels.jobEffect}}</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(group, key) in table">
                        
                        <tr :key="key" v-if="!positiveOnly || anyPositiveRowsInGroup(group)">
                            <td v-if="type !== 'combi'" class="table__inner-title" :colspan="showEffectColumn ? 5 : 4">{{ key }}</td>
                            <td v-else  class="table__inner-title" :colspan="showEffectColumn ? 5 : 4">
                                <ol>
                                    <li v-for="(item, index) in key.split('.')" :key="key + 'combi' + index">{{item}}</li>
                                </ol>
                            </td>
                        </tr>
                        
                        <template v-for="effect in group">
                            <tr :key="effect.id" v-show="!positiveOnly || (effect.algorithmResult.jobeffekt === 0)">
                                <td>
                                    <div>
                                    <a @click="showDetails(effect.searchParameters)" @keyup.enter="showDetails(effect.searchParameters)" class="table__title" :title="getNameOther(effect)" tabindex="0">
                                        <span class="text-truncate icon-circle-right">{{getNameOther(effect)}}</span>
                                    </a>
                                    </div>
                                </td>
                                <td class="effects" v-if="showEffectColumn">
                                    <a @click="getEffectTargets(effect)" @keyup.enter="getEffectTargets(effect)" tabindex="0">
                                        <span class="icon-plus" :class="{'icon-minus': effect.open}"></span>
                                        <span>{{effect.searchParameters.effectTarget.length}} {{labels.filterEffectTargets}} {{ getEffectTypeName(effect.searchParameters) }}</span>
                                    </a>
                                </td>
                                <td class="studies">{{effect.algorithmResult.studyCount}}</td>
                                <td class="stars">
                                    <span class="icon-star icon--effect" 
                                        v-for="e of [1, 2, 3]" 
                                        :key="e" 
                                        :class="{'icon--semi': effect.algorithmResult.evidenceLevel < e }"></span>
                                </td>
                                <td class="jobeffects">
                                    <effect-label :value="effect.algorithmResult.jobeffekt" :mega="false" :type="'label'" :name="effect.algorithmResult.jobeffektName"></effect-label>
                                </td>
                            </tr>

                            <!-- Lazy loaded effect targets -->
                            <template v-if="effect.open">
                                
                                    <template v-if="!effectTargets || !effectTargets[effect.id]">
                                        <tr :key="effect.id">
                                            <td colspan="5" >Indlæser...</td>
                                        </tr>
                                    </template>
                                    <template v-else>
                                        <tr v-for="(childEffect, childKey) in effectTargets[effect.id]" :key="effect.id + childKey" class="effect-row">
                                            <td></td>
                                            <td class="effects">
                                                <a @click="showDetails(childEffect.searchParameters)" @keyup.enter="showDetails(childEffect.searchParameters)" class="table__title icon-circle-right" :title="getEffectTargetName(childEffect.searchParameters)" tabindex="0">
                                                    <span class="text-truncate">{{getEffectTargetName(childEffect.searchParameters)}}</span>
                                                </a>
                                            </td>
                                            <td class="studies">{{childEffect.algorithmResult.studyCount}}</td>
                                            <td class="stars">
                                                <span class="icon-star icon--effect" 
                                                    v-for="e of [1, 2, 3]" 
                                                    :key="e" 
                                                    :class="{'icon--semi': childEffect.algorithmResult.evidenceLevel < e }"></span>
                                            </td>
                                            <td class="jobeffects">
                                                <effect-label :value="childEffect.algorithmResult.jobeffekt" :mega="false" :type="'label'" :name="childEffect.algorithmResult.jobeffektName"></effect-label>
                                            </td>
                                        </tr>
                                    </template>
                            </template>

                        </template>
                    </template>  
                </tbody>
            </table>
        </div>
        <div v-if="positiveOnly && noPositiveResults(searchResult)" class="table__wrapper cell">
            <div class="content__no-results">
                <p class="lead">{{labels.noPositiveJobeffectsWereFound}}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { each, groupBy, orderBy, some, map, compact, flatten, flattenDeep } from 'lodash'

export default {
    props: ["result", "showEffectColumn", "showSortOptions", "type", "labels", "searchComboLoading"],
    data() {
        return {
            sortByLabel: "targetGroup"
        }
    },
    computed: {
        searchResult() {
            return this.groups(this.result);
        },
        effectTargets(){
            return this.$store.state.searchEffectTargets;
        },
        searching() {
            return this.$store.state.searching;
        },
        colHeader() {
            return (this.sortByLabel === 'targetGroup') ? this.labels.intervention : this.labels.targetGroup;
        },
        positiveOnly: {
            get() {
                return this.$store.state.positiveOnly;
            },
            set(value){
                this.$store.commit('POSITIVEONLY', value);
            }
        },
        expanded: {
            get() {
                return this.$store.state.expandCombiTable;
            },
            set(value){
                this.$store.commit("EXPANDCOMBITABLE", value)
            }
        }
    },
    mounted() {
        if (this.$route.query.positiveOnly === 'true') {
            this.positiveOnly = true;
        }
    },
    methods: {
        groups(data) {
            let type = (this.sortByLabel === 'targetGroup') ? 'activity' : 'targetGroup';
            var _sortedData = {};
            if (data !== null && data !== undefined) {

                // sort
                _sortedData = orderBy(data, ['algorithmResult.evidenceLevel', 'algorithmResult.studyCount'], ['desc', 'desc']);
                
                // group by target group
                _sortedData = groupBy(_sortedData, (group) => { return group.searchParameters[this.sortByLabel][0].namePicked });

                // group each target group by activity
                each(_sortedData, (group, key) => {
                    _sortedData[key] = groupBy(group, (item) => {
                        let name =  [];
                        each(item.searchParameters[type], ele => name.push(ele.namePicked));
                        return name.join('. ');
                    });
                })
            }
            return _sortedData;
        },
        getEffectTargets(effect){
            effect.open = !effect.open;
            if(this.effectTargets === null || this.effectTargets[effect.id] === undefined){
                this.$store.dispatch("searchEffectTargets", { params: this.getSearchParams(effect.searchParameters), id: effect.id });
            }
        },
        getName(effect) {
            let type = (this.sortByLabel === 'targetGroup') ? 'activity' : 'targetGroup';
            let name = (effect.searchParameters[type] !== null) ? effect.searchParameters[type][0].namePicked : '-';
            return name;
        },
        getNameOther(effect) {
            let age = map(effect.searchParameters.ageGroup, 'namePicked');
            let edu = map(effect.searchParameters.educationLevel, 'namePicked');
            let gender = map(effect.searchParameters.gender, 'namePicked');
            let social = map(effect.searchParameters.socialCondition, 'namePicked');
            let name = compact(flatten([age, gender, edu, social])).join(". ")

            if(name.length === 0){
                name = "Resultat (ingen afgrænsning af målgruppe)";
            }
            return name;
        },
        getEffectTargetName(params){
            let _name = params.effectTarget;
            return (_name !== null && _name.length > 0) ? _name[0].namePicked : "";
        },
        getEffectTypeName(params){
            let _name = params.effectType;
            return (_name !== null && _name.length > 0) ? "(" + _name[0].namePicked + ")" : "";
        },
        anyPositiveRowsInGroup(group){
            return some(group, (item) => {
                return item.algorithmResult.jobeffekt === 0 
            })

        },
        anyPositiveRows(table) {
            return some(table, (item) => {
                return item[0].algorithmResult.jobeffekt === 0 
            })
        },
        showDetails(searchParams) {
            this.$router.push({ name: 'SearchDetails', query: this.getSearchParams(searchParams) })
        },
        getSearchParams(searchParams){
            // activities and effecttargets as arrays 
            return {
                targetGroup: searchParams.targetGroup[0].id,
                gender: searchParams.gender[0].id,
                ageGroup: searchParams.ageGroup[0].id,
                educationLevel: searchParams.educationLevel[0].id,
                effectType: (searchParams.effectType !== null) ? searchParams.effectType[0].id : '',
                socialCondition: searchParams.socialCondition[0].id,
                effectTarget: map(searchParams.effectTarget, "id"),
                activity: map(searchParams.activity, "id"),
                geographicalArea:  searchParams.filterGeographicalArea != null ? searchParams.filterGeographicalArea.id : '',
            }
        },
        togglePositive(value){
            this.positiveOnly = value;
            setTimeout(()=> this.$router.push({ query: { ...this.$route.query, positiveOnly: this.positiveOnly }, params: { savePosition: true } }), 400)
        },
        toggleSortListBy(value){
            this.sortByLabel = value;
        },
        noPositiveResults() {
            let positiveResults = some(this.result, (item) => {
                return item.algorithmResult.jobeffekt === 0 
            })
            return !positiveResults;
        },
    }
}
</script>



// WEBPACK FOOTER //
// src/components/jobeffects-search/Effects.vue